import { cva, type VariantProps } from "class-variance-authority";
import React from "react";

export const variantClassMap = {
  "display-mark": "type-display-mark",
  "display-xl-accent": "type-display-xl-accent",
  "display-hero": "type-display-hero",
  "display-lg": "type-display-lg font-light",
  "display-md": "type-display-md",
  "heading-xl": "type-heading-xl",
  "heading-lg": "type-heading-lg",
  "heading-md": "type-heading-md",
  "heading-sm": "type-heading-sm",
  "body-lg": "type-body-lg",
  "body-md": "type-body-md",
  "body-sm": "type-body-sm",
  "label-md": "type-label-md",
  "label-sm": "type-label-sm",
  "overline": "type-overline",
  "caption": "type-caption",
} as const;

export const textVariants = cva("", {
  variants: {
    variant: variantClassMap,
    tone: {
      inherit: "text-inherit",
      foreground: "text-foreground",
      lede: "text-lede-foreground",
      muted: "text-muted-foreground",
      onDarkMuted: "text-on-dark-muted",
      primary: "text-primary",
      destructive: "text-destructive",
      white: "text-white",
    },
    weight: {
      light: "font-light",
      normal: "font-normal",
      medium: "font-medium",
      semibold: "font-semibold",
      bold: "font-bold",
    },
    align: {
      left: "text-left",
      center: "text-center",
      right: "text-right",
    },
    truncate: {
      true: "truncate",
    },
    display: {
      inline: "inline",
      block: "block",
      inlineBlock: "inline-block",
    },
  },
  defaultVariants: {
    variant: "body-md",
  },
});

type Component = "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "p" | "span" | "label";

export type TextProps = VariantProps<typeof textVariants> & {
  readonly component?: Component;
  readonly id?: string;
  readonly htmlFor?: string;
  readonly text?: string | number | null;
  readonly children?: React.ReactNode;
};

/**
 * @deprecated Use semantic HTML elements with `type-*` utility classes instead.
 * Example: `<h2 className="type-heading-lg text-foreground">`
 */
export default function Text({
  component = "span",
  id,
  htmlFor,
  text = "",
  children,
  variant,
  tone,
  weight,
  align,
  truncate,
  display,
}: TextProps): React.ReactElement {
  return React.createElement(
    component,
    {
      className: textVariants({
        variant,
        tone,
        weight,
        align,
        truncate,
        display,
      }),
      id,
      htmlFor,
    },
    children ?? text,
  );
}
